import { useState, useEffect } from "react";
import { cleanBaselineHtml } from "../../../util/lexical";
import richTextStorage from "../../../util/richTextStorage";
import { trimTablesInHtml } from "../../../common/RichTextEditor/LexicalEditor/utils/tablePasteUtils";

/** Apply cleanBaselineHtml and trim tables to max 10 columns for display. */
function processHtmlForDisplay(html) {
  if (!html || typeof html !== "string") return html || "";
  const cleaned = cleanBaselineHtml(html);
  return trimTablesInHtml(cleaned);
}

/**
 * useRichTextPersistence Hook
 * 
 * Manages rich text form persistence and draft saving
 * Handles both localStorage (add mode) and IndexedDB (edit mode)
 * 
 * @param {Object} params
 * @param {string} params.mode - "add" or "edit"
 * @param {string} params.id - Rich text ID (for edit mode)
 * @param {Object} params.richTextData - Fetched rich text data
 * 
 * @returns {Object} Form state and persistence methods
 */
const useRichTextPersistence = ({
  mode,
  id,
  richTextData,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    shortDescription: "",
    content: "",
    categoryId: "",
    categoryName: "",
  });
  
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Initialize form data based on mode
  useEffect(() => {
    if (mode === "add") {
      // Add mode: Try to restore from localStorage
      // Use node-type-specific keys to avoid conflicts with other node types (e.g., Canvas)
      try {
        const savedDesc = localStorage.getItem("rich_text_short_description");
        if (savedDesc && savedDesc !== "undefined") {
          setFormData(prev => ({ ...prev, shortDescription: processHtmlForDisplay(savedDesc) }));
        }

        const savedContent = localStorage.getItem("rich_text_content");
        if (savedContent && savedContent !== "undefined") {
          setFormData(prev => ({ ...prev, content: processHtmlForDisplay(savedContent) }));
        }

        const savedCategoryId = localStorage.getItem("rich_text_category_id");
        if (savedCategoryId && savedCategoryId !== "undefined" && savedCategoryId !== "null") {
          setFormData(prev => ({ ...prev, categoryId: savedCategoryId }));
        }

        const savedCategoryName = localStorage.getItem("rich_text_category_name");
        if (savedCategoryName && savedCategoryName !== "undefined" && savedCategoryName !== "null") {
          setFormData(prev => ({ ...prev, categoryName: savedCategoryName }));
        }

        // Check if we have any saved data
        if (savedDesc || savedContent || savedCategoryId || savedCategoryName) {
          setHasUnsavedChanges(true);
        }
        setDataLoaded(true);
      } catch (err) {
        console.error("Error restoring add-mode rich text draft:", err);
        setDataLoaded(true);
      }
    } else if (mode === "edit" && richTextData) {
      // Edit mode: Initialize from fetched data
      // Process short description to handle both plain text and HTML content
      let shortDesc = richTextData?.detail?.short_description || richTextData?.short_description || "";
      if (shortDesc && !shortDesc.includes('<div class="editor-shell">')) {
        // If it's plain text (no editor-shell wrapper), convert it to HTML format
        shortDesc = `<p>${shortDesc}</p>`;
      }
      
      const cleanedShortDesc = cleanBaselineHtml(shortDesc);
      
      const content = richTextData?.detail?.rich_text_content || richTextData?.rich_text_content || "";
      const categoryId = richTextData?.detail?.category_info?.category_id || richTextData?.category_info?.category_id || "";
      const categoryName = richTextData?.detail?.category_info?.category_name || richTextData?.category_info?.category_name || "";
      
      // Check if we're returning from preview (which might have draft data)
      const isReturningFromPreview = localStorage.getItem("editRichTextReturningFromPreview") === "true";
      
      // If returning from preview, try to restore from IndexedDB first
      if (isReturningFromPreview && id) {
        richTextStorage.getRichText(id).then((draftData) => {
          if (draftData) {
            // Use draft data if available (trim tables to 10 cols for legacy content)
            const draftDesc = draftData.shortDescription || cleanedShortDesc;
            const draftContent = draftData.content || content;
            setFormData({
              shortDescription: trimTablesInHtml(draftDesc),
              content: trimTablesInHtml(draftContent),
              categoryId: draftData.categoryId || categoryId,
              categoryName: draftData.categoryName || categoryName,
            });
            // Don't set hasUnsavedChanges here - let the component handle it
          } else {
            // No draft, use API data (trim tables to 10 cols for legacy content)
            setFormData({
              shortDescription: trimTablesInHtml(cleanedShortDesc),
              content: trimTablesInHtml(content),
              categoryId: categoryId,
              categoryName: categoryName,
            });
          }
          setDataLoaded(true);
          localStorage.removeItem("editRichTextReturningFromPreview");
        }).catch(() => {
          // On error, use API data (trim tables to 10 cols for legacy content)
          setFormData({
            shortDescription: trimTablesInHtml(cleanedShortDesc),
            content: trimTablesInHtml(content),
            categoryId: categoryId,
            categoryName: categoryName,
          });
          setDataLoaded(true);
          localStorage.removeItem("editRichTextReturningFromPreview");
        });
      } else {
        // Normal load, use API data directly (trim tables to 10 cols for legacy content)
        setFormData({
          shortDescription: trimTablesInHtml(cleanedShortDesc),
          content: trimTablesInHtml(content),
          categoryId: categoryId,
          categoryName: categoryName,
        });
        setDataLoaded(true);
      }
    }
  }, [mode, richTextData]);

  // Auto-save for edit mode
  useEffect(() => {
    if (mode === "edit" && id && dataLoaded && hasUnsavedChanges) {
      const timeoutId = setTimeout(async () => {
        try {
          await richTextStorage.storeRichText(
            id,
            formData.content,
            formData.shortDescription,
            formData.categoryId,
            formData.categoryName
          );
        } catch (err) {
          console.error("Auto-save (edit) to IndexedDB failed:", err);
        }
      }, 700);
      
      return () => clearTimeout(timeoutId);
    }
  }, [mode, id, dataLoaded, hasUnsavedChanges, formData.content, formData.shortDescription, formData.categoryId, formData.categoryName]);

  // Save for preview (both modes)
  const saveForPreview = async () => {
    if (mode === "add") {
      // Add mode: Save to localStorage
      // Use node-type-specific keys to avoid conflicts with other node types (e.g., Canvas)
      localStorage.setItem("rich_text_short_description", formData.shortDescription);
      localStorage.setItem("rich_text_content", formData.content);
      localStorage.setItem("rich_text_category_id", formData.categoryId);
      localStorage.setItem("rich_text_category_name", formData.categoryName);
    } else {
      // Edit mode: Save to IndexedDB
      await richTextStorage.storeRichText(
        id,
        formData.content,
        formData.shortDescription,
        formData.categoryId,
        formData.categoryName
      );
      localStorage.setItem("editRichTextReturningFromPreview", "true");
    }
  };

  // Clear stored data
  const clearStoredData = async () => {
    try {
      if (mode === "add") {
        // Use node-type-specific keys to avoid conflicts with other node types (e.g., Canvas)
        localStorage.removeItem("rich_text_short_description");
        localStorage.removeItem("rich_text_content");
        localStorage.removeItem("rich_text_category_id");
        localStorage.removeItem("rich_text_category_name");
      } else {
        await richTextStorage.deleteRichText(id);
      }
    } catch (error) {
      console.error("Error clearing stored data:", error);
    }
  };

  // Update form data
  // skipUnsavedChanges: if true, don't automatically set hasUnsavedChanges
  const updateFormData = (updates, skipUnsavedChanges = false) => {
    setFormData(prev => ({ ...prev, ...updates }));
    if (!skipUnsavedChanges) {
      setHasUnsavedChanges(true);
    }
  };

  // Direct setter for formData that doesn't trigger hasUnsavedChanges
  const setFormDataSilent = (updates) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  return {
    formData,
    setFormData: updateFormData,
    setFormDataSilent,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    saveForPreview,
    clearStoredData,
    dataLoaded,
  };
};

export default useRichTextPersistence;
